/**
 * Constructs the hierarchical path for a given folder.
 * @param {string} folderId - The ID of the folder to get the path for.
 * @param {Array<Object>} allFolders - The flat list of all folder objects.
 * Each folder object must have `id` and `parentId`.
 * @returns {Array<string>} An array of folder names representing the path,
 * from the top-level ancestor to the given folder. Returns an empty array if
 * the folderId is not found or if it's a top-level folder with no valid name.
 */
export const getFolderPath = (folderId, allFolders) => {
  const path = [];
  let currentFolderId = folderId;

  // Create a map for quick folder lookup by id
  const foldersMap = new Map();
  allFolders.forEach(folder => foldersMap.set(folder.id, folder));

  while (currentFolderId) {
    const folder = foldersMap.get(currentFolderId);
    if (!folder) {
      // This might happen if folderId is invalid or data is inconsistent
      console.warn(`Folder with ID ${currentFolderId} not found while building path.`);
      break;
    }
    path.unshift(folder.name); // Add folder name to the beginning of the path array
    currentFolderId = folder.parentId; // Move to the parent
  }
  return path;
};

/**
 * Calculates the maximum depth of any folder in the hierarchy.
 * @param {Array<Object>} allFolders - The flat list of all folder objects.
 * @returns {number} The maximum depth (e.g., 1 for top-level only, 2 for one level of subfolders, etc.)
 */
export const getMaxFolderPathDepth = (allFolders) => {
  if (!allFolders || allFolders.length === 0) {
    return 0;
  }
  let maxDepth = 0;
  allFolders.forEach(folder => {
    const depth = getFolderPath(folder.id, allFolders).length;
    if (depth > maxDepth) {
      maxDepth = depth;
    }
  });
  return maxDepth;
};
